import { useCallback, useContext } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import type { DrawerScreen } from '@/types/screens';
import { isInlineScreenSurface } from '@/utils/screenSurface';

/**
 * Opens a sub-screen on whichever surface the active template uses, and tells
 * the caller which one that is so it can render the matching chrome (a back
 * arrow for the in-place surface, a title + Cancel button for the drawer).
 *
 * Closing goes through `drawer.closeDrawer` from context, which clears the same
 * surface this hook writes to — callers don't need a second branch for it.
 */
export const useScreenSurface = () => {
  const {
    template,
    drawer: { setDrawerScreen },
    viewer: { setViewer },
  } = useContext(CheckoutContext);

  const isInlineScreen = isInlineScreenSurface(template.type);

  const openScreen = useCallback(
    (screen: DrawerScreen) => {
      if (isInlineScreen) {
        setViewer(screen);
      } else {
        setDrawerScreen(screen);
      }
    },
    [isInlineScreen, setViewer, setDrawerScreen]
  );

  return { isInlineScreen, openScreen };
};
